/**
 * Hook reusavel para polling com setInterval.
 *
 * Comportamento:
 *  - Executa `fn` imediatamente no mount + a cada `intervaloMs`.
 *  - Pausa quando `document.hidden=true` (aba em background) — evita
 *    desperdicio de request quando ninguem ta olhando.
 *  - Cleanup automatico em unmount (clearInterval).
 *  - Mantem ref atualizada do `fn` pra usar versao mais recente sem
 *    re-criar setInterval a cada render (otimizacao classica).
 *  - `deps` reinicia o timer se mudar.
 *  - `opts.delayPrimeiroMs` (Bloco LOG2 Camada D, 2026-05-25): atrasa o
 *    primeiro tick para reduzir simultaneidade na cascata pos-login.
 *    Sem isso, login dispara 10 requests simultaneas (loadMe + permissoes
 *    + widgets + sino + WhatsApp), saturando o backend Render Free
 *    durante warm-up. Polling normal continua no intervalo `intervaloMs`
 *    depois desse delay inicial.
 *
 * Anti-padroes evitados:
 *  - NAO usa setTimeout recursivo (race condition em cleanup).
 *  - NAO chama fn antes do mount (efeitos rodam apos commit).
 *  - NAO tenta cancelar request HTTP em vigor — caller deve usar
 *    AbortController se quiser (custom; raramente necessario aqui).
 */

import { useEffect, useRef } from "react";

export default function useWhatsappPolling(fn, intervaloMs = 10000, deps = [], opts = {}) {
  const { delayPrimeiroMs = 0 } = opts;
  const fnRef = useRef(fn);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    if (!intervaloMs) return undefined;
    let cancelado = false;
    let primeiroTimer = null;
    let intervalId = null;

    const tick = async () => {
      if (cancelado) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        await fnRef.current();
      } catch (_err) {
        // Silencioso. Auto-toast global do AuthContext.request ja cobre
        // mensagens de erro; nao polui console com fail recorrente.
      }
    };

    const iniciarLoop = () => {
      if (cancelado) return;
      tick(); // primeira execucao real
      intervalId = setInterval(tick, intervaloMs);
    };

    if (delayPrimeiroMs > 0) {
      primeiroTimer = setTimeout(iniciarLoop, delayPrimeiroMs);
    } else {
      iniciarLoop();
    }

    return () => {
      cancelado = true;
      if (primeiroTimer) clearTimeout(primeiroTimer);
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervaloMs, delayPrimeiroMs, ...deps]);
}
